import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { ChangeRow, PayPeriod } from '@/api/types'
import { ChangeCell } from '@/components/ChangeCell'

type Who = 'all' | 'field' | 'office'

const WHO: { key: Who; label: string }[] = [
  { key: 'all', label: 'Everyone' },
  { key: 'field', label: 'Supervisors' },
  { key: 'office', label: 'Office' },
]

/** "2026-11-03" -> "Tue 3 Nov", read as parts so it never shifts a day. */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(y, m - 1, d).getDay()]
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${wd} ${d} ${mon}`
}

/**
 * Changes by week — every correction to time in one pay week.
 *
 * Asked for by Tim Brickell (2 Nov 2026): "It may be good to have one central
 * report showing all changes in a week." Each crew day keeps its own
 * Correction history; this gathers them so a week's changes can be read in
 * one place before it goes to Sage, and filtered to the supervisors' own
 * changes — the ones the supervisor cutoff governs.
 *
 * A day moved between weeks shows in the week it now sits in, with the move.
 */
export function ChangesPage() {
  const [periods, setPeriods] = useState<PayPeriod[]>([])
  const [weekEnding, setWeekEnding] = useState('')
  const [rows, setRows] = useState<ChangeRow[] | null>(null)
  const [who, setWho] = useState<Who>('all')
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .payPeriods()
      .then((p) => {
        setPeriods(p)
        if (p.length > 0) setWeekEnding(p[0].week_ending)
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Could not load pay periods'),
      )
  }, [])

  useEffect(() => {
    if (!weekEnding) return
    setRows(null)
    setError(null)
    api
      .weekChanges(weekEnding)
      .then(setRows)
      .catch((err) => {
        setRows([])
        setError(err instanceof ApiError ? err.message : 'Could not load changes')
      })
  }, [weekEnding])

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (rows ?? []).filter((r) => {
      if (who === 'field' && r.by_office) return false
      if (who === 'office' && !r.by_office) return false
      if (!q) return true
      return [r.employee_name, r.actor_name, r.job_no, r.job_description, r.work_type_label, r.reason]
        .some((v) => v?.toLowerCase().includes(q))
    })
  }, [rows, who, search])

  const period = periods.find((p) => p.week_ending === weekEnding)
  const fieldCount = (rows ?? []).filter((r) => !r.by_office).length

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>Changes by week</h2>
        <p className="muted">
          Every change made to recorded time in a pay week (Sunday to Saturday),
          newest first: hours corrected, lines added or removed, per diem
          unticked, days moved. Open a row's crew day to see it in context.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-field" style={{ marginBottom: 0, minWidth: 240 }}>
            <label htmlFor="chg-week">Week ending</label>
            <select id="chg-week" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)}>
              {periods.map((p) => (
                <option key={p.week_ending} value={p.week_ending}>
                  {dayLabel(p.week_starting)} – {dayLabel(p.week_ending)}
                  {p.paid_in_sage ? ' · paid' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ marginBottom: 0, flex: '1 1 200px' }}>
            <label htmlFor="chg-search">Search</label>
            <input
              id="chg-search"
              type="search"
              placeholder="Person, job, work type or note"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div
          className="segmented"
          style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '0.75rem', maxWidth: 420 }}
        >
          {WHO.map((w) => (
            <button
              key={w.key}
              type="button"
              aria-selected={who === w.key}
              onClick={() => setWho(w.key)}
            >
              {w.label}
            </button>
          ))}
        </div>
        {rows !== null && (
          <p className="muted" style={{ marginBottom: 0 }}>
            {rows.length} change{rows.length === 1 ? '' : 's'} this week · {fieldCount} by
            supervisors · {rows.length - fieldCount} by the office
            {period?.paid_in_sage ? ` · Sage paid this week on ${period.check_date}` : ''}
          </p>
        )}
      </div>

      <div className="card">
        {rows === null ? (
          <p className="muted">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="muted">
            {rows.length === 0 ? 'No changes recorded this week.' : 'Nothing matches the filter.'}
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Job · day</th>
                  <th>Crew member</th>
                  <th>Work type</th>
                  <th>Change</th>
                  <th>Reason</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.audit_id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(r.action_utc + 'Z').toLocaleString(undefined, {
                        weekday: 'short', month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </td>
                    <td>
                      {r.actor_name ?? '—'}
                      {r.by_office && <span className="badge badge-info" style={{ marginLeft: 6 }}>Office</span>}
                    </td>
                    <td>
                      <span className="mono">{r.job_no}</span>{' '}
                      <span className="muted">{dayLabel(r.date_worked)}</span>
                    </td>
                    <td>{r.employee_name ?? '—'}</td>
                    <td>{r.work_type_label ?? '—'}</td>
                    <td><ChangeCell a={r} /></td>
                    <td className="muted">{r.reason ?? '—'}</td>
                    <td>
                      <Link to={`/office/submissions/${r.submission_id}`}>Crew day</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
